import { computed, nextTick, onBeforeUnmount, onMounted, reactive } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import {
  AI_ASSISTANT_TYPE,
  AI_ASSISTANT_ENTRY_CLASS_ROOM,
  AI_ASSISTANT_ENTRY_TOOL,
  requestAiAssistantGenerate,
  AiAssistantContext,
  AiAssistantContentDTO,
} from './classroomAiApi';
import {
  CLASSROOM_AI_PHASES,
  DrawState,
  IdeaBatch,
  IdeaItem,
  LoadingBatchState,
} from './classroomAiData';

function formatTime(date = new Date()): string {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

function mapContentToItems(list: AiAssistantContentDTO[] = []): IdeaItem[] {
  return list
    .map((item, idx) => ({
      index: Number(item.index ?? idx + 1),
      question: String(item.question ?? '').trim(),
      keyword: String(item.keyword ?? '').trim(),
    }))
    .filter((item) => item.question);
}

function buildIdeaBatch(
  label: string,
  tag: string,
  items: IdeaItem[],
  extra?: Partial<IdeaBatch>,
): IdeaBatch {
  return {
    type: 'ideas',
    label,
    tag,
    items,
    time: formatTime(),
    ...extra,
  };
}

export function useFloatingClassroomAi() {
  const route = useRoute();
  const store = useStore();

  const state = reactive({
    panelOpen: false,
    activePhaseId: 'observe',
    batches: [] as IdeaBatch[],
    inspireLoading: false,
    loadingBatchState: null as LoadingBatchState | null,
    drawStateByKey: {} as Record<string, DrawState>,
    drawPreviewVisible: false,
    drawPreviewUrl: '',
    ideaListEl: null as HTMLElement | null,
  });

  const visibleBatches = computed(() => state.batches);

  const scrollIdeaListToBottom = (): void => {
    nextTick(() => {
      const list = state.ideaListEl;
      if (list) {
        list.scrollTop = list.scrollHeight;
      }
    });
  };

  const scrollToLoadingAnchor = (batchIndex: number): void => {
    nextTick(() => {
      const list = state.ideaListEl;
      if (!list) {
        return;
      }
      const target = list.querySelector(`[data-loading-anchor="${batchIndex}"]`) as HTMLElement | null;
      if (target) {
        list.scrollTo({
          top: Math.max(target.offsetTop - 6, 0),
          behavior: 'smooth',
        });
        return;
      }
      scrollIdeaListToBottom();
    });
  };

  const activePhase = computed(() => {
    return CLASSROOM_AI_PHASES.find((phase) => phase.id === state.activePhaseId) || CLASSROOM_AI_PHASES[0];
  });

  const coursewareIndex = computed(() => {
    return Number(store.getters.getCoursewareIndex ?? 0);
  });

  const apiContext = computed((): AiAssistantContext => {
    const fromSubject = route.query.from === 'subject';
    const classScheduleId = fromSubject ? undefined : String(route.query.scheduleId ?? '');
    const topicId = String(route.query.topicId ?? '');

    return {
      classScheduleId: classScheduleId || undefined,
      courseId: fromSubject
        ? String(route.query.courseId ?? route.query.id ?? '') || undefined
        : null,
      topicId: topicId || undefined,
      index: String(coursewareIndex.value + 1),
      entryEnum: fromSubject ? AI_ASSISTANT_ENTRY_TOOL : AI_ASSISTANT_ENTRY_CLASS_ROOM,
    };
  });

  function drawKey(batchIndex: number, itemIndex: number): string {
    return `${batchIndex}_${itemIndex}`;
  }

  function getDrawState(batchIndex: number, itemIndex: number): DrawState | undefined {
    return state.drawStateByKey[drawKey(batchIndex, itemIndex)];
  }

  function isDrawBusy(batchIndex: number, itemIndex: number): boolean {
    const drawState = getDrawState(batchIndex, itemIndex);
    return Boolean(drawState?.loading || drawState?.refreshing);
  }

  function togglePanel(): void {
    state.panelOpen = !state.panelOpen;
  }

  function closePanel(): void {
    state.panelOpen = false;
  }

  function openDrawPreview(url: string): void {
    if (!url) {
      return;
    }
    state.drawPreviewUrl = url;
    state.drawPreviewVisible = true;
  }

  function closeDrawPreview(): void {
    state.drawPreviewVisible = false;
    state.drawPreviewUrl = '';
  }

  function jumpToBatch(targetIndex: number): void {
    const list = state.ideaListEl;
    if (!list || targetIndex < 0) {
      return;
    }
    const target = list.querySelector(`[data-batch-anchor="${targetIndex}"]`) as HTMLElement | null;
    if (!target) {
      return;
    }
    list.scrollTo({
      top: Math.max(target.offsetTop - 6, 0),
      behavior: 'smooth',
    });
  }

  function bindIdeaListRef(el: HTMLElement | null): void {
    state.ideaListEl = el;
  }

  function onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && state.drawPreviewVisible) {
      closeDrawPreview();
    }
  }

  onMounted(() => {
    document.addEventListener('keydown', onKeydown);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', onKeydown);
  });

  async function requestIdeas(
    type: number,
    options: {
      tag?: string;
      question?: string;
      keyword?: string;
    } = {},
  ): Promise<{ tag: string; items: IdeaItem[]; imgUrl?: string }> {
    const res = await requestAiAssistantGenerate({
      ...apiContext.value,
      type,
      tag: options.tag,
      question: options.question,
      keyword: options.keyword,
    });

    return {
      tag: String(res.tag ?? options.tag ?? ''),
      items: mapContentToItems(res.contentDTOList),
      imgUrl: res.imgUrl,
    };
  }

  async function onInspire(): Promise<void> {
    if (state.inspireLoading) {
      return;
    }

    state.inspireLoading = true;
    scrollIdeaListToBottom();
    try {
      const { tag, items } = await requestIdeas(AI_ASSISTANT_TYPE.INSPIRE);
      if (!items.length) {
        return;
      }
      state.batches.push(buildIdeaBatch('灵光一闪', tag, items));
      scrollIdeaListToBottom();
    } finally {
      state.inspireLoading = false;
    }
  }

  async function onRefreshBatch(batchIndex: number): Promise<void> {
    const batch = state.batches[batchIndex];
    if (!batch || state.loadingBatchState) {
      return;
    }

    state.loadingBatchState = { batchIndex, type: 'refresh' };
    scrollToLoadingAnchor(batchIndex);
    try {
      const { tag, items } = await requestIdeas(AI_ASSISTANT_TYPE.REFRESH, {
        tag: batch.tag,
      });
      if (!items.length) {
        return;
      }
      state.batches.splice(
        batchIndex + 1,
        0,
        buildIdeaBatch('换一批', tag, items),
      );
      scrollIdeaListToBottom();
    } finally {
      state.loadingBatchState = null;
    }
  }

  async function onDeepen(batchIndex: number, item: IdeaItem): Promise<void> {
    const batch = state.batches[batchIndex];
    if (!batch || state.loadingBatchState) {
      return;
    }

    state.loadingBatchState = {
      batchIndex,
      type: 'deepen',
      baseQuestion: item.question,
    };
    scrollToLoadingAnchor(batchIndex);

    try {
      const { tag, items } = await requestIdeas(AI_ASSISTANT_TYPE.DEEPEN, {
        tag: batch.tag,
        question: item.question,
      });
      if (!items.length) {
        return;
      }
      state.batches.splice(
        batchIndex + 1,
        0,
        buildIdeaBatch('继续深挖', tag, items, {
          sourceType: 'deepen',
          sourceBatchIndex: batchIndex,
        }),
      );
      scrollIdeaListToBottom();
    } finally {
      state.loadingBatchState = null;
    }
  }

  async function runDraw(
    batchIndex: number,
    itemIndex: number,
    item: IdeaItem,
    batch: IdeaBatch,
    isRefresh: boolean,
  ): Promise<void> {
    const key = drawKey(batchIndex, itemIndex);
    const prev = state.drawStateByKey[key] || {};
    state.drawStateByKey[key] = {
      ...prev,
      loading: !isRefresh,
      refreshing: isRefresh,
      error: '',
    };

    try {
      const { imgUrl } = await requestIdeas(AI_ASSISTANT_TYPE.DRAW, {
        tag: batch.tag,
        keyword: item.keyword || item.question,
      });

      if (!imgUrl) {
        throw new Error('未返回示意图');
      }

      state.drawStateByKey[key] = {
        url: imgUrl,
        loading: false,
        refreshing: false,
        error: '',
      };
    } catch (error) {
      state.drawStateByKey[key] = {
        ...prev,
        loading: false,
        refreshing: false,
        error: error instanceof Error ? error.message : '示意图生成失败，请稍后重试',
      };
    }
  }

  async function onDraw(batchIndex: number, itemIndex: number, item: IdeaItem): Promise<void> {
    const batch = state.batches[batchIndex];
    if (!batch || isDrawBusy(batchIndex, itemIndex)) {
      return;
    }
    await runDraw(batchIndex, itemIndex, item, batch, false);
  }

  async function onRedraw(batchIndex: number, itemIndex: number, item: IdeaItem): Promise<void> {
    const batch = state.batches[batchIndex];
    if (!batch || isDrawBusy(batchIndex, itemIndex)) {
      return;
    }
    await runDraw(batchIndex, itemIndex, item, batch, true);
  }

  return {
    panelOpen: computed(() => state.panelOpen),
    activePhase,
    visibleBatches,
    inspireLoading: computed(() => state.inspireLoading),
    loadingBatchState: computed(() => state.loadingBatchState),
    bindIdeaListRef,
    drawPreviewVisible: computed(() => state.drawPreviewVisible),
    drawPreviewUrl: computed(() => state.drawPreviewUrl),
    togglePanel,
    closePanel,
    onInspire,
    onRefreshBatch,
    onDeepen,
    onDraw,
    onRedraw,
    getDrawState,
    isDrawBusy,
    jumpToBatch,
    openDrawPreview,
    closeDrawPreview,
  };
}
